import { groupBy, isEmpty, meanBy } from 'lodash-es';
import Rainbow from 'rainbowvis.js';
import { useMemo } from 'react';
import { VenueMapColorSelector } from 'src/components/Events/VenueMap/VenueMap.types';
import { VenueMapContentProps } from 'src/components/Events/VenueMap/VenueMapProps';
import { vars } from 'src/core/themes';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import { roundToPrecision } from 'src/utils/numberFormatter';
import { CompListing, Listing, UiMoney } from 'src/WebApiController';

export const ListingCompsSpectrum = ['0094DC', 'FFFFFF', 'D80F5E'];
export const ListingCompsRangeMin = -10;
export const ListingCompsRangeMax = 10;
/**
 * Number of steps in the spectrum.
 */
export const ListingCompsNumberRange =
  ListingCompsRangeMax - ListingCompsRangeMin + 1;

const STEPS_PER_HALF = (ListingCompsNumberRange - 1) / 2;

/**
 * The ratio that represents the maximum end of the spectrum.
 */
const MAX_RATIO = 2;
const MAX_RATIO_INCREMENT = Math.abs(MAX_RATIO - 1) / STEPS_PER_HALF;
/**
 * The ratio that represents the minimum end of the spectrum.
 */
const MIN_RATIO = 0.5;
const MIN_RATIO_INCREMENT = Math.abs(MIN_RATIO - 1) / STEPS_PER_HALF;

/**
 * Round to 8 decimal places by default.
 */
const ROUNDING_DECIMAL_PLACES = 8;

/**
 * Gets the range index for a comparable price on a scale of 50% to 200% of the given `price`.
 *
 * The value is negated before returning because for the spectrum is actual relative to the `price`
 * instead of the comparable price, so the spectrum is actually reversed.
 * @param price
 * @param comparablePrice
 * @returns The color for the given comparable price.
 */
export function getCompListingSpectrumIndex(
  price: number,
  comparablePrice: number
) {
  // the ratio relative to 100%
  const relativeRatio =
    Math.max(MIN_RATIO, Math.min(comparablePrice / price, MAX_RATIO)) - 1;
  if (relativeRatio > 0) {
    return -Math.ceil(
      roundToPrecision(
        relativeRatio / MAX_RATIO_INCREMENT,
        ROUNDING_DECIMAL_PLACES
      )
    );
  }
  if (relativeRatio < 0) {
    return -Math.floor(
      roundToPrecision(
        relativeRatio / MIN_RATIO_INCREMENT,
        ROUNDING_DECIMAL_PLACES
      )
    );
  }
  return 0;
}

export function useComparableListingsVenueMapColor(
  listings: Listing[],
  compListings?: CompListing[],
  defaultCompListings?: CompListing[],
  disableRainbowColor = false
): NonNullable<VenueMapContentProps['getColor']> {
  const compListingsBySection = useMemo(() => {
    if (!compListings) return {};

    return groupBy(
      compListings.filter((l) => l.section),
      (l) => l.section
    );
  }, [compListings]);
  const compListingsByRowId = useMemo(() => {
    if (!compListings) return {};

    return groupBy(
      compListings.filter((l) => l.rowId),
      (l) => l.rowId
    );
  }, [compListings]);

  const defaultCompListingsBySection = useMemo(() => {
    if (!defaultCompListings) return {};

    return groupBy(
      defaultCompListings.filter((l) => l.section),
      (l) => l.section
    );
  }, [defaultCompListings]);
  const defaultCompListingsByRowId = useMemo(() => {
    if (!defaultCompListings) return {};

    return groupBy(
      defaultCompListings.filter((l) => l.rowId),
      (l) => l.rowId
    );
  }, [defaultCompListings]);

  const sellerListingsBySection = useMemo(
    () =>
      groupBy(
        listings.flatMap(flattenListingGroup) as Listing[],
        (listing) => listing.seating.section
      ),
    [listings]
  );
  const sellerListingsByRowId = useMemo(
    () =>
      groupBy(
        listings.flatMap(flattenListingGroup) as Listing[],
        (listing) => listing.seating.rowId
      ),
    [listings]
  );

  const avgOverallPrice = meanBy(
    (compListings ?? [])
      .map(
        ({ sellerNetProceeds, sellerAllInPrice }) =>
          sellerNetProceeds ?? sellerAllInPrice
      )
      .filter((money): money is UiMoney => money != null),
    ({ amt }) => amt
  );

  return useMemo(() => {
    return ({
      sectionName,
      rowId,
      isSelected,
      isMarked,
    }: VenueMapColorSelector) => {
      if (!sectionName && !rowId) {
        return;
      }

      if (isSelected) {
        return {
          fill: vars.color.backgroundBrand,
          stroke: vars.color.borderActive,
          textColor: vars.color.textInverted,
        };
      }

      if (isMarked) {
        return {
          fill: vars.color.backgroundHighlight,
          stroke: vars.color.borderActive,
          textColor: vars.color.textPrimary,
        };
      }

      const hasCompListing =
        (rowId && !isEmpty(defaultCompListingsByRowId[rowId])) ||
        (sectionName && !isEmpty(defaultCompListingsBySection[sectionName]));

      const selectedComplistings =
        (rowId && compListingsByRowId[rowId]) ??
        (sectionName && compListingsBySection[sectionName]);

      const hasSellerListings =
        (rowId && !isEmpty(sellerListingsByRowId[rowId])) ||
        (sectionName && !isEmpty(sellerListingsBySection[sectionName]));

      if (!disableRainbowColor && selectedComplistings) {
        const avgCompPrice = meanBy(
          selectedComplistings
            .map(
              ({ sellerNetProceeds, sellerAllInPrice }) =>
                sellerNetProceeds ?? sellerAllInPrice
            )
            .filter((money): money is UiMoney => money != null),
          ({ amt }) => amt
        );

        const rainbow = new Rainbow();
        rainbow.setNumberRange(ListingCompsRangeMin, ListingCompsRangeMax);
        rainbow.setSpectrumByArray(ListingCompsSpectrum);

        const color = rainbow.colorAt(
          getCompListingSpectrumIndex(avgCompPrice, avgOverallPrice)
        );
        return {
          fill: `#${color}`,
          stroke: hasSellerListings ? vars.color.backgroundBrand : 'black',
          strokeWidth: hasSellerListings ? '10px' : undefined,
        };
      }

      return {
        fill: vars.color.backgroundPrimary,
        stroke: hasCompListing
          ? vars.color.borderActive
          : hasSellerListings
            ? vars.color.border
            : vars.color.borderLight,
        textColor: hasCompListing
          ? vars.color.textPrimary
          : vars.color.textSecondary,
      };
    };
  }, [
    avgOverallPrice,
    compListingsByRowId,
    compListingsBySection,
    defaultCompListingsByRowId,
    defaultCompListingsBySection,
    disableRainbowColor,
    sellerListingsByRowId,
    sellerListingsBySection,
  ]);
}
